<template>
  <h2 class="pb-half-margin-bottom">
    {{ translate('pb2.requestView.searchSubtitle') }}
  </h2>

  <SanitizedHtml
    :content="translate('pb2.requestView.searchIntro')"
    class="pb-margin-bottom"
  />

  <FlexBox direction="row">
    <BrandsField
      v-model="tempBrand"
      class="pb-request-view-half pb-half-margin-bottom pb-quarter-margin-right"
      alignment="right"
    />
    <PbInput
      v-model="tempName"
      dynamic
      :label="translate('profile.properties.name')"
      class="pb-request-view-half pb-half-margin-bottom pb-quarter-margin-left pb-half-margin-right"
      @keypress.enter="submitSearchHotkey"
    />
    <ControlButton
      class="pb-no-margin-important"
      v-bind="searchProps.submit"
      :disabled="disableSearch"
    />
  </FlexBox>

  <RadioGroup>
    <ProfileList
      v-model="profileUid"
      column-brand
      column-www
      label
      :no-item-found-message="requiresSearch ? translate('pb2.requestView.searchRequiredMessage') : undefined"
    >
      <template #header>
        <TableCell
          class="pb-column-header pb-profiles-list-column-icons"
          head
          center
        >
          <UiIcon
            v-tooltip="{ text: translate('pb2.requestView.viewProfileTooltip') }"
            icon="show"
            color="muted"
            class="pb-icon-cell-item"
          />
        </TableCell>

        <TableCell
          class="pb-column-header pb-profiles-list-column-icons"
          head
          center
        >
          <UiIcon
            v-tooltip="{ text: translate('pb2.downloadProfile.label') }"
            icon="download"
            color="muted"
            class="pb-icon-cell-item"
          />
        </TableCell>

        <TableCell
          class="pb-column-header pb-profiles-list-column-icons"
          head
          center
        >
          <UiIcon
            v-tooltip="{ text: translate('pb2.requestView.reportIssueTooltip') }"
            icon="warning"
            color="muted"
            class="pb-icon-cell-item"
          />
        </TableCell>
      </template>

      <template #default="{ item }">
        <TableCell
          :to="item.link"
          class="pb-profiles-list-column-icons pb-icon-cell"
          center
          muted
        >
          <UiIcon
            v-tooltip="{ text: translate('pb2.requestView.viewProfileTooltip') }"
            class="pb-icon-cell-item"
            icon="show"
          />
        </TableCell>

        <ProfileListDownloadCell
          :item
          muted
        />

        <TableCellBase>
          <BaseInput
            :id="item.uid"
            v-model="profileUid"
            class="pb-request-view-checkable-cell"
            :checked-value="item.uid"
            :type="INPUT_TYPE_RADIO"
          />
        </TableCellBase>
      </template>
    </ProfileList>
  </RadioGroup>

  <h2 class="pb-half-margin-bottom pb-double-margin-top">
    {{ translate('pb2.requestView.requestTypeSubtitle') }}
  </h2>
  <FlexBox direction="row">
    <RadioGroup>
      <UiField class="pb-request-view-half pb-half-margin-bottom pb-quarter-margin-right pb-request-view-checkable-field">
        <template #label>
          <div class="pb-request-view-checkbox-label checkbox-label">
            <strong>{{ name ? translate('pb2.requestView.newProfileOption.label').template({ name }) : translate('pb2.requestView.newProfileOption.labelEmpty') }}</strong><br>
            <span class="txt-muted">{{ translate('pb2.requestView.newProfileOption.description') }}</span>
          </div>
        </template>
        <BaseInput
          v-model="repair"
          :checked-value="false"
          :type="INPUT_TYPE_RADIO"
        />
      </UiField>
      <UiField class="pb-request-view-half pb-half-margin-bottom pb-quarter-margin-left pb-request-view-checkable-field">
        <template #label>
          <div class="pb-request-view-checkbox-label checkbox-label">
            <strong>{{ translate('pb2.requestView.repairOption.label') }}</strong><br>
            <span class="txt-muted">{{ lastProfileName }}</span>
          </div>
        </template>
        <BaseInput
          v-model="repair"
          :checked-value="true"
          :type="INPUT_TYPE_RADIO"
        />
      </UiField>
    </RadioGroup>
  </FlexBox>

  <FlexBox direction="row">
    <ControlButton
      :to="{ name: routeNames.Home }"
      :tooltip="translate('pb2.requestView.cancelButton.tooltip')"
      tooltip-location="bottom"
      class="pb-create-profile-link"
      :aria-label="translate('pb2.requestView.cancelButton.tooltip')"
      :label="translate('pb2.requestView.cancelButton.label')"
    />
    <ButtonWithLoader
      class="btn-success"
      tooltip-location="bottom"
      :tooltip="translate('pb2.requestView.continueButton.tooltip')"
      :aria-label="translate('pb2.requestView.continueButton.tooltip')"
      :label="translate('pb2.requestView.continueButton.label')"
      :disabled="requiresSearch || requiresRepairSelection || listLoading"
      :to="continueLink"
    />
  </FlexBox>
</template>

<script setup>
import {
  computed,
  watch,
} from 'vue'
// import useHotkeys, { KEYS } from '@nicolaudie-group/ng-ui-kit/composables/useHotkeys'
import vTooltip from '@nicolaudie-group/ng-ui-kit/directives/vTooltip'
import ControlButton from '@nicolaudie-group/ng-ui-kit/components/ControlButton/ControlButton.vue'
import FlexBox from '@nicolaudie-group/ng-ui-kit/components/FlexBox/FlexBox.vue'
import UiField from '@nicolaudie-group/ng-ui-kit/components/UiField/UiField.vue'
import BaseInput from '@nicolaudie-group/ng-ui-kit/components/BaseInput/BaseInput.vue'
import { INPUT_TYPE_RADIO } from '@nicolaudie-group/ng-ui-kit/components/BaseInput/symbols'
import RadioGroup from '@nicolaudie-group/ng-ui-kit/components/RadioGroup/RadioGroup.vue'
import SanitizedHtml from '@nicolaudie-group/ng-ui-kit/components/SanitizedHtml/SanitizedHtml.vue'
// import * as sdk from '@/helpers/sdk'
// import useAction from '@/composables/useAction'
import BrandsField from '@/components/BrandsField/BrandsField.vue'
import PbInput from '@/components/PbInput.vue'
import ButtonWithLoader from '@/components/ButtonWithLoader.vue'
import routeNames from '@/router/names'
// import useProfileStore from '@/stores/profile.store'
import useTranslate from '@/composables/useTranslate'
import ProfileList from '@/components/ProfileList/ProfileList.vue'
import useProfileList, { FILTER_CERTIFIED } from '@/composables/useProfileList'
import TableCellBase from '@nicolaudie-group/ng-ui-kit/components/Table/TableCellBase.vue'
import TableCell from '@nicolaudie-group/ng-ui-kit/components/Table/TableCell.vue'
import UiIcon from '@nicolaudie-group/ng-ui-kit/components/UiIcon/UiIcon.vue'
import ProfileListDownloadCell from '@/components/ProfileList/ProfileListDownloadCell.vue'
// import { useDebounceFn } from '@vueuse/core'
import { useRouteQuery } from '@vueuse/router'
import { useProfile } from '@/composables/useProfile'
// import TableCellSelect from '@nicolaudie-group/ng-ui-kit/components/Table/TableCellSelect.vue'

const { translate } = useTranslate()

const {
  loading: listLoading,
  filter,
  search,
} = useProfileList({
  filterQuery: false,
  paginationQuery: false,
  sortQuery: false,
  searchQuery: true,
})

filter.filter.value = FILTER_CERTIFIED

const {
  tempBrand,
  tempName,
  brand,
  name,
  // hasSearched,
  searchEnabled,
  // applySearch,
  searchProps,
} = search

// const debouncedSearch = useDebounceFn(applySearch, 500)

const allFieldsSet = computed(() => (
  tempBrand.value && tempName.value
))

const requiresSearch = computed(() => {
  if (!allFieldsSet.value) {
    // Require the user to search if either the brand or name field is not set
    return true
  }
  if (searchEnabled.value) {
    // Require the user to search if one of the fields has changed since last search
    return true
  }
  return false
})

const disableSearch = computed(() => (
  searchProps.value.submit.disabled || (searchEnabled.value && !allFieldsSet.value)
))

// watch([tempBrand, tempName], () => {
//   if (allFieldsSet.value && searchEnabled.value) {
//     debouncedSearch()
//   }
// })

const repair = useRouteQuery('action', false, {
  mode: 'replace',
  transform: {
    get: (value) => (value === 'repair'),
    set: (value) => (value ? 'repair' : 'new'),
  },
})

const profileUid = useRouteQuery('profileUid', null, {
  mode: 'replace',
})

const fixtureToRepair = useProfile()

/*
Cache the name of the last selected profile.
This is so that when the user selects a different profile to repair,
we avoid a flash while the data of the profile is loading.
*/
const lastProfileName = computed((oldValue) => {
  // Reset the cached profile name when the profile uid is cleared (ie. request for new profile)
  if (!profileUid.value) return translate('pb2.requestView.repairOption.description')
  // Otherwise, if a profile is loaded, return it's name
  const profile = fixtureToRepair.value
  if (profile) return profile.name
  // Or if the profile has not loaded (yet) return the last cached value
  return oldValue
})

watch(profileUid, (newProfileUid) => {
  // If the user selected a profile to repair but was requesting a new profile
  if (newProfileUid && !repair.value) {
    // Request a repair instead
    repair.value = true
  }
})

watch(repair, (newRepair) => {
  if (!newRepair) {
    profileUid.value = null
  }
})

const requiresRepairSelection = computed(() => (repair.value && !fixtureToRepair.value))

function submitSearchHotkey() {
  if (!disableSearch.value) {
    search.applySearch()
  }
}

const continueLink = computed(() => {
  if (repair.value) {
    return {
      name: routeNames.RequestRepair,
      params: {
        profileUid: fixtureToRepair.value?.uid,
      },
    }
  }
  return {
    name: routeNames.RequestNew,
    query: {
      brand: brand.value,
      name: name.value,
    },
  }
})

</script>

<style lang="scss">
@use '@nicolaudie-group/ng-ui-kit/defines/_fonts.define.scss';
@use '@nicolaudie-group/ng-ui-kit/defines/_colors.define.scss';
@use '@nicolaudie-group/ng-ui-kit/defines/_config.define.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_flexbox.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_generic.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_responsive.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_theme.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_animations.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_transitions.mixin.scss';

@use '@nicolaudie-group/ng-ui-kit/components/UiField/field.mixin.scss';

@use '/src/styles/_pb.scss';

.pb-request-view-half {
  width: 0;
  max-width: 50%;
}

.pb-request-view-support-message-field {
  flex-direction: column;

  .ui-field-label {
    max-width: initial;
  }
}

.pb-request-view-checkable-cell {
  display: flex;
  text-align: center;
  height: 100%;
}

.pb-request-view-checkable-field {
  .ui-field-label {
    max-width: initial;
    flex-shrink: 1;
  }
}

.pb-request-view-checkbox-label {
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
